/**
 * Shared Framer Motion variants for the Duolingo-style web redesign.
 *
 * All variants honor `prefers-reduced-motion` indirectly: callers should
 * read `useReducedMotion()` and pass either the real variant or `{}`.
 *
 * Each variant is tuned to feel "tactile" — short durations (200-450ms),
 * soft spring or easeOut curves, no exaggerated bounces that block reading.
 */

// ---- Reusable easings ------------------------------------------------------
export const EASE_OUT = [0.22, 0.61, 0.36, 1];
export const EASE_IN_OUT = [0.65, 0, 0.35, 1];
export const SPRING_SOFT = { type: 'spring', stiffness: 220, damping: 22, mass: 0.9 };
export const SPRING_BOUNCY = { type: 'spring', stiffness: 320, damping: 16, mass: 0.7 };

// ---- Generic enter animations ---------------------------------------------
export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE_OUT },
  },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4, ease: EASE_OUT } },
};

export const slideInLeft = {
  hidden: { opacity: 0, x: -32 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: EASE_OUT } },
};

export const slideInRight = {
  hidden: { opacity: 0, x: 32 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: EASE_OUT } },
};

export const bounceIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: SPRING_BOUNCY },
};

// ---- Stagger containers ----------------------------------------------------
export const staggerContainer = (stagger = 0.06, delayChildren = 0.05) => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

// ---- Interactive states ----------------------------------------------------
// Card hover/tilt — small lift + soft shadow shift; pair with clay-card-hover CSS.
export const cardHover = {
  rest: { y: 0, scale: 1 },
  hover: { y: -4, scale: 1.015, transition: { duration: 0.2, ease: EASE_OUT } },
  tap: { y: 0, scale: 0.985, transition: { duration: 0.1, ease: EASE_OUT } },
};

// Button squash — Duolingo-style press-down feedback (works alongside .clay-btn:active).
export const buttonSquash = {
  rest: { scale: 1, y: 0 },
  hover: { scale: 1.02, transition: { duration: 0.15, ease: EASE_OUT } },
  tap: { scale: 0.94, y: 2, transition: { duration: 0.08, ease: EASE_OUT } },
};

// Input shake — for invalid form fields. Use as `animate` once on error.
export const inputShake = {
  shake: {
    x: [0, -6, 6, -5, 5, -3, 3, 0],
    transition: { duration: 0.45, ease: EASE_IN_OUT },
  },
};

// Success bounce — for submit confirm, lesson complete, XP earned, etc.
export const successBounce = {
  hidden: { opacity: 0, scale: 0.6 },
  visible: {
    opacity: 1,
    scale: [0.6, 1.15, 0.95, 1],
    transition: { duration: 0.55, ease: EASE_OUT, times: [0, 0.5, 0.8, 1] },
  },
};

// Floating idle — for decorative icons (e.g. mascot placeholders, hero stickers).
export const floatIdle = {
  animate: {
    y: [0, -8, 0],
    transition: { duration: 3.2, ease: EASE_IN_OUT, repeat: Infinity },
  },
};

// Flame pulse — for streak fire icon, subtle bob + scale.
export const flamePulse = {
  animate: {
    y: [0, -3, 0],
    scale: [1, 1.08, 1],
    transition: { duration: 2.4, ease: EASE_IN_OUT, repeat: Infinity },
  },
};

// ---- Viewport defaults -----------------------------------------------------
// Use with `whileInView` so animations fire when scrolled into view (not on mount).
export const VIEWPORT_ONCE = { once: true, amount: 0.25 };

// ---- Helper: collapse a variant when reduced motion is on ------------------
// Usage: `variants={maybe(reduced, fadeUp)}`
export const maybe = (reduced, variant) => (reduced ? undefined : variant);
